import {
  PRODUCT_TYPES, PRODUCT_COLOR_IDS, subtypesOf, isSubtypeOf,
} from './productTaxonomy';

// ── GALLERY: photos and published 3D designs ─────────────────────────────────
// Both item kinds behave identically from the user's side — a picture, a short
// description, a visibility switch and a delete — so they share one set of
// limits, one service and one UI. Only the collection they live in differs.

export const ITEM_KINDS = { PHOTO: 'photo', DESIGN: 'design' };

// Collection names per kind. Metadata and image bytes are deliberately split:
// listing a gallery reads only the metadata documents (each carrying a small
// thumbnail), so opening a profile never downloads full-size images nobody
// asked to see. The full rendition is fetched one document at a time.
export const COLLECTIONS = {
  [ITEM_KINDS.PHOTO]:  { meta: 'photos',          blob: 'photoBlobs'  },
  [ITEM_KINDS.DESIGN]: { meta: 'publishedDesigns', blob: 'designBlobs' },
};

export const GALLERY_LIMITS = {
  description: 200,        // per the spec
  title: 80,
  productUrl: 300,
  price: 100000000,        // an upper bound, not a currency-specific one
  // Images in ONE post. Five is the cap the UI enforces and the write path
  // slices to, so a hand-built payload cannot exceed it either.
  imagesPerItem: 5,
};

// ── HOW MANY ITEMS SOMEONE MAY PUBLISH ───────────────────────────────────────
// NOT HERE ANY MORE. It used to be `GALLERY_LIMITS.maxItems: 60`, a flat number
// every account got regardless of what it paid for — which made
// `portfolioImages` in constants/plans.js decoration: the pricing page promised
// 15 on free and Unlimited on gold, and both were silently given 60.
//
// The cap now comes from the plan (see GalleryManager), so plans.js is the one
// place it is written. What stays here is the unrelated number below.

// ── HOW MANY DOCUMENTS ONE LISTING QUERY READS ───────────────────────────────
// A FETCH CEILING, NOT A PLAN LIMIT, and the distinction matters: the two were
// the same constant before and so could not be reasoned about separately.
//
// Every metadata document carries a ~640px thumbnail (see below), so this is
// the knob that decides how heavy opening a profile is — at roughly 40-80KB a
// thumbnail, 120 is already a 5-10MB listing in the worst case. It is a
// ceiling on BYTES, and it does not care which plan the owner is on.
//
// ⚠️ GOLD IS "UNLIMITED" ONLY UP TO THIS NUMBER. Publishing past it is allowed
// and the documents are written, but a listing query stops here, so items past
// the 120th newest do not appear. Making Infinity literally true needs
// pagination — a cursor on `createdAt`, which this query is already ordered by
// — and that is a feature, not a constant. Until then this is deliberately set
// well above the largest FINITE plan cap (silver's 60) so no paying account can
// quietly hit it in normal use.
export const GALLERY_FETCH_MAX = 120;

// ── A POST'S IMAGES ──────────────────────────────────────────────────────────
// A post is one to five pictures. The METADATA document carries only the
// COVER's thumbnail, and that is the whole reason this scales: a 60-item feed
// query reads 60 thumbnails whether or not those posts hold five pictures
// each. Putting all five in the metadata would have turned a ~2MB feed into a
// ~9MB one to show pictures nobody has asked to see yet.
//
// The full renditions live one per document in the blob collection, fetched
// only as the carousel reaches them — exactly what the single-image viewer
// already did, extended to a list.
//
// `blobIds[0]` IS THE ITEM'S OWN ID for the cover, so every document written
// before this existed is already correct: no `blobIds` means one image, stored
// at the id it always was. Both helpers below encode that, and nothing else in
// the app needs to know about the migration.
export const blobIdsOf = (item) =>
  (Array.isArray(item?.blobIds) && item.blobIds.length ? item.blobIds : [item?.id]).filter(Boolean);

export const imageCountOf = (item) => blobIdsOf(item).length;

// ── PRODUCT FIELDS ───────────────────────────────────────────────────────────
// A price and a link to where the thing can actually be bought. Written only by
// SHOPS: a photo published by a shop is a product listing, and the two extra
// fields are what make it one.
//
// THEY LIVE ON THE PHOTO, they are not a separate kind. A product listing is a
// picture with a description that can be liked, viewed, commented on and shared
// — everything the photo kind already is. Splitting it out would have meant a
// second collection pair, a second set of security rules, a second social
// target, a second feed source and a second gallery grid, all to carry two
// optional fields. Both are null on a designer's photo and nothing renders.
//
// Serialized here rather than in the service so the form, the write path and
// the security rules all agree on what "a price" is.
export function serializeProductFields(f = {}) {
  const price = f.price === '' || f.price == null ? null : Number(f.price);

  // WHAT THE THING IS. Filtered to the taxonomy rather than trusted: a stored
  // type that is not in the list is invisible to every filter, which is worse
  // than no type at all because it looks filled in.
  const type = PRODUCT_TYPES.includes(f.type) ? f.type : '';
  // A subtype only survives with the type it belongs to. Changing the type in
  // the form leaves the old subtype behind in state, and `electronics / parquet`
  // would otherwise be written straight through.
  const subtype = type && isSubtypeOf(type, f.subtype) ? f.subtype : '';
  const color = PRODUCT_COLOR_IDS.includes(f.color) ? f.color : '';

  return {
    type,
    subtype,
    color,
    price: Number.isFinite(price) && price >= 0
      ? Math.min(Math.round(price * 100) / 100, GALLERY_LIMITS.price)
      : null,
    // Only meaningful alongside a price; stored as the code, displayed as the
    // symbol (see currencySymbolOf).
    currency: String(f.currency || '').trim().slice(0, 8),
    // Kept as typed — normalising to a URL happens at render time, so a shop
    // that pastes a bare domain still gets a working link without its stored
    // value being rewritten underneath it.
    productUrl: String(f.productUrl || '').trim().slice(0, GALLERY_LIMITS.productUrl),
  };
}

/** A stored product link as a clickable href. Bare domains get a scheme. */
export function productHref(url) {
  const v = String(url || '').trim();
  if (!v) return '';
  return /^https?:\/\//i.test(v) ? v : `https://${v}`;
}

// A bare domain counts — productHref adds the scheme — but it has to look like
// a domain, so "my product" is rejected while "shop.com/chair" is not.
const PRODUCT_URL_RE = /^(https?:\/\/)?[^\s/.]+\.[^\s]{2,}$/i;

/**
 * What a listing must have before it can be published.
 *
 * THE PRICE IS REQUIRED. A product with no price is the one thing a shop's
 * grid must not contain: it is what every visitor is scanning for, and a tile
 * without one is a picture pretending to be a listing. Zero and negative are
 * refused along with blank — a shop quoting nothing should leave the item
 * unpublished rather than list it at 0.
 *
 * THE LINK STAYS OPTIONAL. Plenty of shops sell from a counter and have no
 * product page to point at; requiring one would keep real listings off the
 * site. It is only checked when something was actually typed.
 *
 * Returns an i18n-key map, like the designer and shop profile validators.
 */
export function validateProductFields(f) {
  const errors = {};

  // TYPE AND SUBTYPE ARE REQUIRED, because they are the axis product search
  // runs on: an untyped listing is reachable only by someone already looking
  // at the shop that published it, which is the opposite of being listed.
  if (!PRODUCT_TYPES.includes(f?.type)) errors.type = 'prod_err_type';
  else if (subtypesOf(f.type).length && !isSubtypeOf(f.type, f?.subtype)) {
    errors.subtype = 'prod_err_subtype';
  }

  // Colour too — `multicolor` exists so that a patterned rug or a mixed set
  // has a true answer, which is what makes requiring it fair.
  if (!PRODUCT_COLOR_IDS.includes(f?.color)) errors.color = 'prod_err_color';

  const raw = f?.price;
  const n = raw === '' || raw == null ? NaN : Number(raw);
  if (!Number.isFinite(n) || n <= 0) errors.price = 'gal_err_price';
  else if (n > GALLERY_LIMITS.price) errors.price = 'gal_err_price_max';

  const url = String(f?.productUrl || '').trim();
  if (url && !PRODUCT_URL_RE.test(url)) errors.productUrl = 'gal_err_link';

  return errors;
}

/** Whether a stored item carries a usable price. */
export const hasPrice = (item) => Number.isFinite(item?.price) && item.price > 0;

// Full rendition — what a visitor sees when they open a photo.
//
// THE CEILING IS NOT NEGOTIABLE: a Firestore document is capped at 1,048,576
// bytes, and base64 inflates binary by ~4/3. So the largest image that can be
// stored is roughly 1MB × 3/4 ≈ 786KB of actual JPEG, before field-name
// overhead. The budget below sits under that with headroom to spare.
//
// Within that budget the aim is to SPEND it: 2048px at q0.86 uses the space on
// resolution and detail, where the previous 1600px/q0.78 often produced files
// far under the cap — paying the same storage cost for a softer image.
export const PHOTO_FULL_MAX_PX    = 2048;
export const PHOTO_FULL_QUALITY   = 0.86;   // starting point; lowered to hit the target
export const PHOTO_FULL_MAX_BYTES = 700 * 1024;   // absolute refusal point

// The size compression actually AIMS for. WebP reaches this at a visually
// indistinguishable quality where JPEG could not — typically 25-35% smaller for
// the same picture, which is what makes ~200KB realistic at 2048px instead of
// the 1-3MB a phone camera produces.
export const PHOTO_TARGET_BYTES   = 200 * 1024;

// Absolute stop measured on the STORED string, not the decoded image — this is
// the number Firestore actually enforces, and the one a decoded-size check
// silently gets wrong by a third.
export const FIRESTORE_DOC_SAFE_CHARS = 1000 * 1024;

// Thumbnail — grid tile. Rides along on the metadata document, so every one is
// fetched when a gallery is listed. Raised to suit the larger 3-per-row tiles
// (~360px, so 640 stays crisp on retina) while keeping a 60-item listing sane.
export const PHOTO_THUMB_MAX_PX  = 640;
export const PHOTO_THUMB_QUALITY = 0.75;
